import { useMemo } from "react";
import { Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import MDBox from "../../controls/MDBox";
import MDTypography from "../../controls/MDTypography";
import MDButton from "../../controls/MDButton";
import { COLOR_VARIOS, DIAS, colorOrganismo, diaDeHoy } from "./calendarioComun";

const BORDE = "2px solid #263238";

// Matriz del Calendario de Gestores: un renglón por gestor y una columna por día de la semana; cada celda dice a qué organismo le
// toca ese día (con el color del organismo). Si le tocan varios el mismo día, los muestra juntos. Es pública: la ve cualquier usuario.
function CalendarioMatriz({ open, onClose, filas }) {
  const hoy = diaDeHoy();

  // [{ idUsuario, usuario, dias: { 1: [{ idOrganismo, organismo }], ... } }], por orden alfabético del gestor.
  const gestores = useMemo(() => {
    const porGestor = new Map();
    (filas ?? [])
      .filter((f) => f.usuarioActivo !== false)
      .forEach((f) => {
        if (!porGestor.has(f.idUsuario)) porGestor.set(f.idUsuario, { idUsuario: f.idUsuario, usuario: f.usuario, dias: {} });
        const dias = porGestor.get(f.idUsuario).dias;
        (dias[f.diaSemana] ??= []).push({ idOrganismo: f.idOrganismo, organismo: f.organismo });
      });
    // Los organismos de un mismo día siempre salen ordenados por nombre, vengan como vengan los datos.
    porGestor.forEach((g) =>
      Object.values(g.dias).forEach((organismos) => organismos.sort((a, b) => (a.organismo ?? "").localeCompare(b.organismo ?? "", "es", { sensitivity: "base" })))
    );
    return [...porGestor.values()].sort((a, b) => a.usuario.localeCompare(b.usuario));
  }, [filas]);

  // Una celda de la matriz: vacía si ese día no le toca ningún organismo; si le toca uno, con su color; si le tocan varios, con el color
  // de «varios» (que ningún organismo usa) y los nombres separados por « / ».
  const celda = (dia, organismos) => {
    if (!organismos?.length) return <TableCell key={dia} sx={{ border: BORDE, backgroundColor: "#ffffff" }} />;
    const color = organismos.length === 1 ? colorOrganismo(organismos[0].idOrganismo) : COLOR_VARIOS;
    return (
      <TableCell key={dia} align="center" sx={{ border: BORDE, backgroundColor: color.fondo, py: 1.2, px: 1 }}>
        <MDTypography variant="button" fontWeight="bold" sx={{ color: color.texto, textTransform: "uppercase", lineHeight: 1.2 }}>
          {organismos.map((o) => o.organismo).join(" / ")}
        </MDTypography>
      </TableCell>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Calendario de Gestores
        <MDTypography display="block" variant="caption" color="text" fontWeight="regular">
          A qué organismo le toca gestionar a cada gestor cada día de la semana. El Disparador le asigna primero casos de ese organismo.
        </MDTypography>
      </DialogTitle>
      <DialogContent dividers>
        {gestores.length === 0 ? (
          <MDTypography variant="button" color="text">
            Todavía no hay gestores cargados en el calendario.
          </MDTypography>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ borderCollapse: "collapse" }}>
              {/* El tema del proyecto le pone display:block al TableHead y el encabezado se sale de las columnas del cuerpo. */}
              <TableHead sx={{ display: "table-header-group", p: 0 }}>
                <TableRow>
                  <TableCell sx={{ border: BORDE, backgroundColor: "#d9ead3" }}>
                    <MDTypography variant="button" fontWeight="bold">
                      GESTORES / DÍAS
                    </MDTypography>
                  </TableCell>
                  {DIAS.map((dia) => (
                    <TableCell key={dia.valor} align="center" sx={{ border: BORDE, backgroundColor: dia.valor === hoy ? "#fff2cc" : "#d9ead3" }}>
                      <MDTypography variant="button" fontWeight="bold" sx={{ textTransform: "uppercase" }}>
                        {dia.nombre}
                      </MDTypography>
                      {dia.valor === hoy && (
                        <MDTypography variant="caption" display="block" color="text">
                          hoy
                        </MDTypography>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {gestores.map((g) => (
                  <TableRow key={g.idUsuario}>
                    <TableCell sx={{ border: BORDE, backgroundColor: "#d9ead3" }}>
                      <MDTypography variant="button" fontWeight="bold" sx={{ textTransform: "uppercase" }}>
                        {g.usuario}
                      </MDTypography>
                    </TableCell>
                    {DIAS.map((dia) => celda(dia.valor, g.dias[dia.valor]))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <MDBox mt={2} display="flex" alignItems="center" gap={1}>
          <MDBox component="span" sx={{ width: 18, height: 18, borderRadius: "3px", border: "1px solid #263238", backgroundColor: COLOR_VARIOS.fondo }} />
          <MDTypography variant="caption" color="text">
            Más de un organismo el mismo día (se muestran separados por « / »).
          </MDTypography>
        </MDBox>
        <MDBox mt={1}>
          <MDTypography variant="caption" color="text">
            Si a un gestor no le toca ningún organismo un día (celda vacía), ese día se le asignan casos de cualquier organismo. Si ya no
            quedan casos del organismo del día, se completa por puntaje con casos de otros organismos.
          </MDTypography>
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant="outlined" color="info" onClick={onClose}>
          Cerrar
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

export default CalendarioMatriz;
